import {useEffect, useRef, useState} from 'react';
import {Button, Stack} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import {useTranslation} from 'react-i18next';
import {Media} from '../../../../../backend/interfaces/media';
import {MediaType} from '../../../../../backend/enums/media-type';
import {MediaAlbum} from '../../../../../backend/enums/media-album';
import {useSnackBar} from '../../../../../contexts/snack-bar/context';
import {translations} from '../../../../../i18n/translations';
import {MediaRepository} from '../../../../../repositories/media';

export default function MediaExportActions({
  media,
  seniorId,
  isCurrent,
}: {
  media: Media;
  seniorId: string;
  isCurrent: () => boolean;
}) {
  const {t} = useTranslation();
  const snackBar = useSnackBar();
  const currentRef = useRef(isCurrent);
  currentRef.current = isCurrent;
  const [file, setFile] = useState<File>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const prepare = async () => {
      setFile(undefined);
      setFailed(false);
      try {
        const fresh = await MediaRepository.instance.getById(media.id, controller.signal);
        if (controller.signal.aborted || !currentRef.current()) return;
        if (
          fresh.id !== media.id ||
          fresh.seniorUserId !== seniorId ||
          fresh.album !== MediaAlbum.Shared ||
          fresh.type !== media.type ||
          !fresh.url
        )
          throw new Error('Media is unavailable');
        const response = await fetch(fresh.url, {signal: controller.signal});
        if (!response.ok) throw new Error('Could not fetch media');
        const blob = await response.blob();
        if (controller.signal.aborted || !currentRef.current()) return;
        const extensions: Record<string, string> = {
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/gif': 'gif',
          'image/webp': 'webp',
          'image/avif': 'avif',
          'video/mp4': 'mp4',
          'video/webm': 'webm',
          'video/quicktime': 'mov',
        };
        const extension = extensions[blob.type] || (media.type === MediaType.Video ? 'mp4' : 'jpg');
        setFile(
          new File(
            [blob],
            `sioslife-${media.type === MediaType.Video ? 'video' : 'photo'}.${extension}`,
            {type: blob.type}
          )
        );
      } catch {
        if (!controller.signal.aborted && currentRef.current()) setFailed(true);
      }
    };
    void prepare();
    return () => controller.abort();
  }, [media.id, media.type, seniorId, attempt]);

  const download = () => {
    if (!file || !currentRef.current()) return;
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const share = () => {
    if (!file || !currentRef.current()) return;
    void navigator.share({files: [file]}).catch((error: unknown) => {
      if (!(error instanceof DOMException && error.name === 'AbortError') && currentRef.current()) {
        void snackBar.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.family_media.export_error),
        });
      }
    });
  };
  const canShare =
    file && typeof navigator.canShare === 'function' && navigator.canShare({files: [file]});
  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{color: 'common.white', justifyContent: 'flex-end'}}
    >
      {failed ? (
        <Button color="inherit" onClick={() => setAttempt((value) => value + 1)}>
          {t(translations.family_media.retry)}
        </Button>
      ) : (
        <>
          <Button color="inherit" startIcon={<DownloadIcon />} disabled={!file} onClick={download}>
            {t(translations.family_media.download)}
          </Button>
          {typeof navigator.share === 'function' && (!file || canShare) && (
            <Button
              color="inherit"
              startIcon={<ShareOutlinedIcon />}
              disabled={!file}
              onClick={share}
            >
              {t(translations.family_media.share)}
            </Button>
          )}
        </>
      )}
    </Stack>
  );
}
